<template>
  <BaseModal :modalTitle="$t('W: Kontaktperson')">
    <!-- Step: select an existing admin user -->
    <UiBaseFormVee
      v-if="currentStep === 'select'"
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmitSelect">
      <UiComboboxVee
        name="primaryContactUserID"
        :label="$t('W: Kontaktperson')"
        :options="adminUserOptions"
        v-model="form.selectedUserID" />

      <button
        type="button"
        class="mt-2 cursor-pointer text-sm text-action hover:underline"
        @click="currentStep = 'create'">
        {{ $t('W: Opret ny bruger i stedet') }}
      </button>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>

    <!-- Step: create a new admin user -->
    <UiBaseFormVee
      v-else
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmitCreate">
      <UiInputTextFieldVee
        name="firstName"
        :label="$t('form.label.first-name')"
        :placeholder="$t('form.placeholder.first-name')"
        :rules="['required']"
        isRequired
        v-model="form.firstName" />

      <UiInputTextFieldVee
        name="lastName"
        :label="$t('form.label.last-name')"
        :placeholder="$t('form.placeholder.last-name')"
        :rules="['required']"
        isRequired
        v-model="form.lastName" />

      <UiInputTextFieldVee
        name="email"
        :label="$t('form.label.email')"
        :placeholder="$t('form.placeholder.email')"
        :rules="['required', 'email']"
        isRequired
        type="email"
        v-model="form.email">
        <template v-if="EMU.isEmulatingFirestore" #trailingIcon>
          <button
            type="button"
            class="pointer-events-auto flex h-5 w-5 cursor-pointer items-center justify-center opacity-50 hover:opacity-100"
            v-tooltip="$t('W: Indsæt dummy-email')"
            @click="insertFakeEmail">
            <IconLucideSparkles class="h-5 w-5" />
          </button>
        </template>
      </UiInputTextFieldVee>

      <UiInputPhoneVee
        name="phone"
        :label="$t('form.label.phone')"
        :rules="['requiredPhone', 'phone']"
        isRequired
        v-model="form.phone" />

      <template #buttons>
        <UiBaseButton
          :text="$t('W: Tilbage')"
          theme="neutral"
          :isDisabled="isLoading"
          @button-click="currentStep = 'select'" />
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useModalStore } from '@/stores/modalStore'
import { customerKey } from '@/globals/typescript/utils/customerOwner'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import { EMU } from '@/globals/typescript/utils/host'
import { useToast } from '@/globals/typescript/utils/toast'
import { getEmailInUseByAnotherUserMessage } from '@/globals/typescript/utils/errorMessages'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'SelectContactPersonModal',

  props: {
    /** The business or insurance customer whose primary contact is being set. */
    customer: { type: Object as PropType<ICustomerRef>, required: true },
    currentPrimaryContactUserID: {
      type: String as PropType<string | null>,
      default: null,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      currentStep: 'select' as 'select' | 'create',
      form: {
        selectedUserID: this.currentPrimaryContactUserID ?? '',
        firstName: '',
        lastName: '',
        email: '',
        phone: { number: 0, iso2: 'dk' },
      },
      isLoading: false,
      errorText: '',

      EMU,
    }
  },

  computed: {
    ...mapState(useCustomerUserStore, ['customerUsersByCustomerKey']),

    adminUserOptions() {
      const adminUsers = (
        this.customerUsersByCustomerKey[customerKey(this.customer)] ?? []
      )
        .filter((user) => user.mainRole === 'admin' && user.activeState === 'active')
        .map((user) => ({ value: user.id, text: user.fullName }))
        .sort((a, b) => a.text.localeCompare(b.text))

      return [{ value: '', text: this.$t('W: Ingen kontaktperson') }, ...adminUsers]
    },
  },

  methods: {
    ...mapActions(useCustomerUserStore, [
      'getCustomerUsers',
      'createCustomerUser',
    ]),
    ...mapActions(useCustomerAccountStore, ['updateCustomerAccountGeneralInfo']),
    ...mapActions(useInsuranceCustomerAccountStore, [
      'updateInsuranceCustomerAccountGeneralInfo',
    ]),
    ...mapActions(useModalStore, ['closeTop']),

    /** Saves the reference through whichever account the customer is — the same field on both. */
    savePrimaryContact(primaryContactUserID: string | null): Promise<void> {
      return this.customer.type === 'insurance'
        ? this.updateInsuranceCustomerAccountGeneralInfo(this.customer.id, { primaryContactUserID })
        : this.updateCustomerAccountGeneralInfo(this.customer.id, { primaryContactUserID })
    },

    insertFakeEmail(): void {
      this.form.email = generateFakeEmail()
      this.errorText = ''
    },

    async onSubmitSelect(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.savePrimaryContact(this.form.selectedUserID || null)
        useToast().success(this.$t('W: Kontaktperson gemt'))
        this.closeTop()
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },

    async onSubmitCreate(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        const result = await this.createCustomerUser(this.customer, {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          phone: this.form.phone,
          mainRole: 'admin',
        })

        if (!result.success || !result.id) {
          this.errorText =
            result.code === 'already-exists'
              ? getEmailInUseByAnotherUserMessage()
              : this.$t('error.common.standard')
          useToast().error(this.errorText)
          return
        }

        await this.savePrimaryContact(result.id)
        useToast().success(this.$t('W: Kontaktperson gemt'))
        this.closeTop()
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.getCustomerUsers()
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
